import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, DateInput, FormField, Heading, Text, TextInput } from "grommet";
import * as React from "react";
import Avatar from "react-avatar";
import StyledModal from "../components/StyledModel";
import { useReferenceData } from "../reference/ReferenceStore";
import { getStats, StatType, StatViewModel } from "../stats/StatsStore";
import styled from "styled-components";
import { TeamViewModel } from "../teams/TeamsStore";
import { AddStatRequest, addStat, CreateMatchRequest, MatchStatViewModel, MatchParticipantViewModel, MatchViewModel, RemoveStatRequest, removeStat, updateMatchStat, UpdateStatRequest } from "./MatchesStore";
import { Stat, StatColor, StatIcon } from "../stats/StatIcon";
import { RenderStat } from "../stats/StatRenderer";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;

export const MatchParticipantStats = ({ match, disabled, participant, handleChange }: { match: MatchViewModel; disabled: boolean; participant: MatchParticipantViewModel; handleChange: Function }) => {
    const [showAddStatModal, setShowAddStatModal] = React.useState(false);
    const [showRemoveStatModal, setShowRemoveStatModal] = React.useState(false);

    const [participantStat, setParticipantStat] = React.useState({} as MatchParticipantViewModel);
    const [statToRemove, setStatToRemove] = React.useState({} as MatchStatViewModel);

    const [stat, setStat] = React.useState({} as StatViewModel);
    const [value, setValue] = React.useState(0);

    const [statCreateError, setStatCreateError] = React.useState("");
    const [statRemoveError, setStatRemoveError] = React.useState("");

    const [isLoading, setIsLoading] = React.useState(false);

    const [statTypes, setStatTypes] = React.useState([] as StatViewModel[]);

    React.useEffect(() => {
        getStats().then((response) => {
            setStatTypes(response.result);
        });
    }, []);

    const startAddStat = (participant: MatchParticipantViewModel) => {
        setParticipantStat(participant);
        setShowAddStatModal(true);
    };

    const addStatToMatch = () => {
        setStatCreateError("");
        setIsLoading(true);
        addStat({ matchID: match.id, participantID: participantStat.id, statID: stat.id, value } as AddStatRequest).then(async (response) => {
            if (response.success) {
                setStat({} as StatViewModel);
                setValue(0);
                handleChange();
                setShowAddStatModal(false);
            } else {
                setStatCreateError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const startRemoveStat = (stat: MatchStatViewModel) => {
        setStatToRemove(stat);
        setShowRemoveStatModal(true);
    };

    const removeStatFromMatch = () => {
        setStatRemoveError("");
        setIsLoading(true);
        removeStat({ matchID: match.id, matchStatID: statToRemove.id } as RemoveStatRequest).then((response) => {
            if (response.success) {
                handleChange();
                setShowRemoveStatModal(false);
            } else {
                setStatRemoveError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const [showUpdateStatModal, setShowUpdateStatModal] = React.useState(false);
    const [statToUpdate, setStatToUpdate] = React.useState({} as MatchStatViewModel);
    const [statUpdateError, setStatUpdateError] = React.useState("");
    const [statUpdateValue, setStatUpdateValue] = React.useState(0);

    const startUpdateStat = (stat: MatchStatViewModel) => {
        setStatToUpdate(stat);
        setShowUpdateStatModal(true);
        setStatUpdateValue(stat.value);
    };

    const updateStat = () => {
        setStatUpdateError("");
        updateMatchStat({
            matchID: match.id,
            matchStatID: statToUpdate.id,
            value: statUpdateValue,
        } as UpdateStatRequest).then((res) => {
            handleChange();
        });
    };

    return (
        <>
            <Box gap="medium">
                <Box
                    background="dark-2"
                    pad="medium"
                    round="small"
                    gap="medium"
                    style={{
                        boxShadow: '0 0 15px rgba(0,172,237,0.1)'
                    }}
                >
                    <Box direction="row" gap="small" justify="between" align="center">
                        <Box direction="row" gap="medium" align="center">
                            <Box 
                                width="40px" 
                                height="40px" 
                                style={{
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '2px solid var(--accent,#00aced)'
                                }}
                            >
                                <img 
                                    src={participant.team?.logoUri} 
                                    alt={participant.team?.name} 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                            <Text weight="bold" size="large" color="brand">
                                {participant.team?.name}
                            </Text>
                        </Box>
                        {!disabled && (
                            <Button 
                                icon={plus} 
                                label="Add Stat" 
                                onClick={() => startAddStat(participant)} 
                                disabled={isLoading}
                                primary
                                style={{
                                    background: 'linear-gradient(45deg, var(--accent,#00aced), #0097e6)',
                                    borderRadius: '4px',
                                    transition: 'all 0.2s ease',
                                    transform: 'scale(1)'
                                }}
                                hoverIndicator={{
                                    elevation: 'medium',
                                    background: 'brand'
                                }}
                            />
                        )}
                    </Box>
                    <Box direction="row" gap="small" wrap>
                        {participant.stats.map((matchStat, statIndex) => (
                            <Box
                                key={statIndex}
                                background="dark-1"
                                pad={{ vertical: 'small', horizontal: 'medium' }}
                                round="small"
                                border={{ color: 'dark-3', size: '1px' }}
                                onClick={() => !disabled && startUpdateStat(matchStat)}
                                style={{
                                    transition: 'all 0.2s ease',
                                    cursor: disabled ? 'default' : 'pointer'
                                }}
                                elevation={!disabled && matchStat === statToUpdate ? 'medium' : undefined}
                                hoverIndicator={!disabled ? { color: 'brand', dark: true } : undefined}
                            >
                                <RenderStat 
                                    stat={matchStat.stat} 
                                    value={matchStat.value} 
                                    disabled={disabled}
                                    onClick={() => !disabled && startUpdateStat(matchStat)}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            <StyledModal isOpen={showAddStatModal} setIsOpen={setShowAddStatModal}>
                <Box width={"medium"}>
                    <Box direction="row" align="center" gap="small">
                        <img src={participantStat?.team?.logoUri} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {participantStat?.team?.name}
                    </Box>
                    <Box gap="small" pad="small">
                        {statTypes.map((_stat, statTypeIndex) => (
                            <Button key={statTypeIndex} primary={stat.id == _stat.id} color={StatColor(_stat)} label={_stat.name} icon={StatIcon(_stat)} onClick={() => setStat(_stat)} disabled={isLoading}></Button>
                        ))}
                    </Box>
                    <FormField label="Value">
                        <TextInput type={"number"} value={value} onChange={(e) => setValue(+e.target.value)} />
                    </FormField>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statCreateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowAddStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => addStatToMatch()} label="Add" disabled={isLoading || !stat.id}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showUpdateStatModal} setIsOpen={setShowUpdateStatModal}>
                <Box width={"medium"}>
                    <Heading size="small">{statToUpdate?.stat?.name}</Heading>
                    <FormField label="Value">
                        <TextInput type={"number"} value={statUpdateValue} onChange={(e) => setStatUpdateValue(+e.target.value)} />
                    </FormField>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statUpdateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowUpdateStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => updateStat()} label="Update" disabled={isLoading}></Button>
                    </Box>
                    <Button onClick={() => startRemoveStat(statToUpdate)} label="Remove Stat" color="status-critical" disabled={isLoading}></Button>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveStatModal} setIsOpen={setShowRemoveStatModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this stat?</Text>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => removeStatFromMatch()} label="Remove" color="status-critical" disabled={isLoading}></Button>
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};

export default MatchParticipantStats;
